'use client';

import React, { useMemo, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import Drawer from '@/components/organisms/drawer';
import Button from '@/components/atoms/button';
import { classnames } from '@/utils/classnames/classnames';
import { AddToPurchaseListDrawerProps, PurchaseList } from './types';

// EWM-188 right-side drawer launched from a product card.
//
// Lets the dealer toggle the product into one or more existing purchase
// lists (commercetools Shopping Lists). Selection is internal — the
// drawer fires `onSubmit` with the chosen list ids on Done; the caller
// performs the batch write at qty=0 (zero-default per Workshop 6).
//
// EWM-601 — an inline "+ New list" affordance sits above the existing lists
// (only when `onCreateList` is supplied). Clicking it expands an inline input
// (no modal); submitting creates the list via `onCreateList`, then shows and
// auto-selects it so the user can add the product straight away; Cancel
// collapses without creating.
//
// Pure presentational: caller owns isOpen and supplies the lists + the
// create handler (which performs the commercetools write).
const AddToPurchaseListDrawer = ({
  isOpen,
  product,
  lists,
  onSubmit,
  onCreateList,
  onClose,
}: AddToPurchaseListDrawerProps) => {
  const translate = useTranslations();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // EWM-601 — inline create state.
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Lists created in this drawer session, kept locally so they show + stay
  // selected immediately — before the caller's data revalidation catches up.
  const [createdLists, setCreatedLists] = useState<PurchaseList[]>([]);

  // EWM-760 (was a setState-in-effect on `[isOpen]`) — adjust-state-during-
  // render: reset all transient state each time the drawer reopens. Same
  // guard (`if (isOpen)`) and the same trigger (`isOpen` changing) as the
  // old effect, just applied synchronously in the same render.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setSelected(new Set());
      setIsCreating(false);
      setNewName('');
      setSubmitting(false);
      setCreatedLists([]);
    }
  }

  // Locally-created lists render first (newest at top), then the caller's
  // lists — deduped by id so a created list doesn't double up once the
  // caller's revalidated `lists` includes it.
  const displayLists = useMemo(() => {
    const seen = new Set(createdLists.map((l) => l.id));
    return [...createdLists, ...lists.filter((l) => !seen.has(l.id))];
  }, [createdLists, lists]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDone = () => {
    onSubmit?.(Array.from(selected));
  };

  const cancelCreate = () => {
    setIsCreating(false);
    setNewName('');
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    const created = await onCreateList?.(trimmed);
    setSubmitting(false);
    if (!created) return; // creation failed — keep the input open (caller toasts)
    setCreatedLists((prev) => [created, ...prev]);
    setSelected((prev) => new Set(prev).add(created.id));
    setIsCreating(false);
    setNewName('');
  };

  return (
    <Drawer
      isOpen={isOpen}
      headline={translate('product.add-to-list')}
      direction="right"
      onClose={onClose ?? (() => undefined)}
    >
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto">
          <div className="border-b border-neutral-300 px-4 py-4 lg:px-5">
            <p className="text-11 font-semibold tracking-wide text-gray-500 uppercase">
              {translate('wishlist.adding-product')}
            </p>
            <p className="text-14 mt-1 font-semibold text-gray-800">{product.name}</p>
            <p className="text-12 mt-0.5 text-gray-500">
              <span>{translate('common.cat-no')}:</span> {product.sku}
            </p>
          </div>

          <div className="px-4 py-4 lg:px-5">
            <p className="text-11 mb-3 font-semibold tracking-wide text-gray-500 uppercase">
              {translate('wishlist.your-lists')}
            </p>

            {/* EWM-601 — inline "+ New list" affordance above the existing lists. */}
            {onCreateList &&
              (isCreating ? (
                <div className="border-primary mb-2 flex items-center gap-2 rounded border px-3 py-2">
                  <input
                    type="text"
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleCreate();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelCreate();
                      }
                    }}
                    placeholder={translate('common.list-name')}
                    aria-label={translate('common.list-name')}
                    className="text-14 focus:ring-primary/40 min-w-0 flex-1 rounded border border-neutral-300 px-2 py-1.5 focus:ring-2 focus:outline-hidden"
                  />
                  <Button
                    variant="primary"
                    size="xs"
                    onClick={() => void handleCreate()}
                    disabled={newName.trim().length === 0 || submitting}
                  >
                    {translate('common.save')}
                  </Button>
                  <Button variant="secondary" size="xs" onClick={cancelCreate} disabled={submitting}>
                    {translate('common.cancel')}
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsCreating(true)}
                  className="text-14 text-primary hover:bg-primary/5 mb-2 flex w-full items-center gap-2 rounded border border-dashed border-neutral-300 px-3 py-3 font-semibold"
                >
                  <PlusIcon className="size-4" aria-hidden />
                  {translate('wishlist.new-list')}
                </button>
              ))}

            {displayLists.length === 0 ? (
              <p className="text-14 text-gray-500">{translate('wishlist.no-lists')}</p>
            ) : (
              <ul role="listbox" aria-multiselectable className="flex flex-col gap-1">
                {displayLists.map((list) => {
                  const isSelected = selected.has(list.id);
                  return (
                    <li key={list.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => toggle(list.id)}
                        className={classnames(
                          'text-14 flex w-full items-center justify-between rounded border px-3 py-3 text-left',
                          isSelected
                            ? 'border-primary bg-primary/5 text-gray-800'
                            : 'border-neutral-300 hover:bg-neutral-100',
                        )}
                      >
                        <span className="font-semibold">{list.name}</span>
                        <span className="text-12 text-gray-500">
                          — {translate('wishlist.list-items-count', { count: list.itemCount })}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="border-t border-neutral-300 p-4 lg:p-5">
          <Button variant="primary" size="m" onClick={handleDone} disabled={selected.size === 0} className="w-full">
            {translate('product.done')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default AddToPurchaseListDrawer;
